import '../styles/adminResponsive.css';
import DiscoveryModeration from '../components/DiscoveryModeration';
import ApplicationMetrics from '../components/ApplicationMetrics';
import { AdminProRequests } from '../components/ProRequest';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, ShieldAlert, Gem } from 'lucide-react';
import {
  EVENT_TYPE_LABELS, DAY_LABELS, TIME_RANGE_LABELS, TECH_LABELS,
  BUDGET_MODEL_LABELS,
} from '../constants/canonicalEnums';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
  new: 'Yeni',
  admin_reviewing: 'Admin İnceliyor',
  contacted: 'İletişime Geçildi',
  matched: 'Eşleşti',
  rejected: 'Reddedildi',
};

const STATUS_FILTERS = [
  { value: 'new', label: 'Yeni', color: 'bg-sky-100 text-sky-900' },
  { value: 'admin_reviewing', label: 'İnceleniyor', color: 'bg-violet-100 text-violet-900' },
  { value: 'contacted', label: 'İletişimde', color: 'bg-amber-100 text-amber-900' },
  { value: 'matched', label: 'Eşleşti', color: 'bg-emerald-600 text-white' },
  { value: 'rejected', label: 'Reddedildi', color: 'bg-zinc-100 text-zinc-600' },
];

const labelize = (v, table) => (v ? (table[v] || v) : '—');
const labelList = (arr, table) => (arr && arr.length ? arr.map(v => table[v] || v).join(', ') : '—');

const FiltersSummary = ({ applied, extracted }) => {
  if (!applied && !extracted) return null;
  const a = applied || {};
  const rows = [
    ['event_type', 'Etkinlik türü', labelize(a.event_type, EVENT_TYPE_LABELS)],
    ['expected_attendees', 'Katılımcı', a.expected_attendees || '—'],
    ['preferred_districts', 'İlçeler', (a.preferred_districts || []).join(', ') || '—'],
    ['preferred_days', 'Günler', labelList(a.preferred_days, DAY_LABELS)],
    ['preferred_time_ranges', 'Saatler', labelList(a.preferred_time_ranges, TIME_RANGE_LABELS)],
    ['venue_budget_model', 'Bütçe', labelize(a.venue_budget_model, BUDGET_MODEL_LABELS)],
    ['technical_needs', 'Teknik', labelList(a.technical_needs, TECH_LABELS)],
  ];
  return (
    <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 text-xs">
      <p className="font-bold text-zinc-700 mb-2">Uygulanan Filtreler</p>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
        {rows.map(([k, l, v]) => (
          <div key={k}><span className="text-zinc-500">{l}: </span><span className="font-medium">{v}</span></div>
        ))}
      </div>
    </div>
  );
};

const HistoryTimeline = ({ interestId }) => {
  const [history, setHistory] = useState(null);
  useEffect(() => {
    let m = true;
    axios.get(`${API}/api/admin/interests/${interestId}/history`)
      .then(({ data }) => { if (m) setHistory(data.history || []); })
      .catch(() => { if (m) setHistory([]); });
    return () => { m = false; };
  }, [interestId]);
  if (history === null) return <p className="text-xs text-zinc-500 italic">Yükleniyor…</p>;
  if (!history.length) return <p className="text-xs text-zinc-500 italic">Henüz olay yok.</p>;
  return (
    <div className="space-y-1.5 max-h-52 overflow-y-auto pr-1">
      {[...history].reverse().map((h, i) => (
        <div key={i} className="text-xs border-l-2 border-zinc-100 pl-3 py-0.5">
          <div className="font-semibold text-zinc-700">{h.event}</div>
          <div className="text-zinc-500">
            {new Date(h.at).toLocaleString('tr-TR')}
            {h.by?.email && ` · ${h.by.email}`}
          </div>
        </div>
      ))}
    </div>
  );
};

const AdminInterests = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('new');
  const [counts, setCounts] = useState({});
  const [openDetails, setOpenDetails] = useState({});
  const [openHistory, setOpenHistory] = useState({});
  const [drafts, setDrafts] = useState({});
  const [busyById, setBusyById] = useState({});
  const saving = useRef(new Set());

  const requestGeneration = useRef(0);
  const fetchItems = useCallback(async () => {
    const generation = ++requestGeneration.current;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/interests?status_filter=${filter}`);
      if (generation === requestGeneration.current) setItems(data || []);
    } catch (err) {
      if (generation === requestGeneration.current) toast.error('Başvurular yüklenemedi.');
    } finally { if (generation === requestGeneration.current) setLoading(false); }
  }, [filter]);

  const fetchCounts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/interests/counts`);
      setCounts(data || {});
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchItems(); return () => { requestGeneration.current += 1; }; }, [fetchItems]);
  useEffect(() => { fetchCounts(); }, [fetchCounts, items]);

  const draftOf = (id, source) => ({
    status: source.status,
    admin_note_internal: source.admin_note_internal || '',
    admin_note_risk: source.admin_note_risk || '',
    admin_note_for_organizer: source.admin_note_for_organizer || '',
    ...drafts[id],
  });
  const setDraft = (id, patch) => setDrafts(prev => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const save = async (item) => {
    if (saving.current.has(item.id)) return;
    const d = draftOf(item.id, item);
    const submittedDraft = drafts[item.id];
    saving.current.add(item.id);
    setBusyById(p => ({ ...p, [item.id]: true }));
    try {
      // The endpoint requires a status; send only edited notes so unrelated
      // private/risk/reply notes cannot be erased by an empty draft.
      await axios.put(`${API}/api/admin/interests/${item.id}/decide`, { status: d.status, ...submittedDraft, expected_updated_at: item.updated_at || null });
      toast.success('Kaydedildi');
      setDrafts(prev => { if (prev[item.id] !== submittedDraft) return prev; const c = { ...prev }; delete c[item.id]; return c; });
      fetchItems();
    } catch (err) {
      if (err.response?.status === 409) {
        await fetchItems();
        toast.error('Başvuru bu sırada değişmiş. Güncel durum yüklendi; yazdığın yanıt korunuyor. Kontrol edip tekrar kaydet.');
        return;
      }
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : err.response?.status === 422
        ? 'Başvuru bilgileri doğrulanamadı. Yanıtın korunuyor; sayfayı yenilemeden tekrar deneyebilirsin.'
        : 'Kaydedilemedi. Yanıtın korunuyor; tekrar deneyebilirsin.');
    } finally {
      saving.current.delete(item.id);
      setBusyById(p => ({ ...p, [item.id]: false }));
    }
  };

  return (
    <div className="p-6 sm:p-10 max-w-6xl mx-auto admin-responsive-page">
      <div className="mb-8 animate-soft-lift">
        <p className="text-xs text-[#1E3A8A] font-semibold uppercase tracking-wider mb-2">Admin · Başvuru Paneli</p>
        <h1 className="font-serif text-3xl sm:text-4xl tracking-tight text-zinc-900" data-testid="admin-interests-title">
      Organizatör Başvuruları</h1>
        <p className="text-sm text-zinc-600 mt-2 max-w-2xl">Organizatörlerin mekân başvurularını yönet ve yanıtla. Mekân sahibi onayladığında iletişim bilgileri organizatöre açılır; etkinlik koşulları ayrıca netleştirilir.</p>
      </div>

      <AdminProRequests />
      <ApplicationMetrics /><DiscoveryModeration />
      <div className="flex flex-wrap gap-2 mb-6" data-testid="interest-tabs">
        {STATUS_FILTERS.map(s => (
          <button key={s.value} onClick={() => setFilter(s.value)}
            data-testid={`interest-tab-${s.value}`}
            className={`px-4 py-2 text-sm rounded-full font-medium transition-all duration-200 ${
              filter === s.value
                ? 'bg-zinc-900 text-white shadow-sm'
                : 'bg-white border border-zinc-200 hover:border-zinc-300 text-zinc-700'
            }`}>
            {s.label}
            <span className="ml-2 px-2 py-0.5 rounded-full bg-black/10 text-xs tabular-nums">
              {counts[s.value] ?? '·'}
            </span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="p-8 text-center text-zinc-500">Yükleniyor…</div>
      ) : items.length === 0 ? (
        <div className="border-2 border-dashed border-zinc-200 p-12 text-center rounded-2xl">
          <p className="text-sm text-zinc-500">Bu kategoride başvuru yok.</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="admin-interests-list">
          {items.map(it => {
            const d = draftOf(it.id, it);
            const dirty = !!drafts[it.id];
            return (
              <div key={it.id} className="bg-white border-2 border-zinc-200 rounded-2xl p-6 admin-responsive-card" data-testid={`admin-interest-${it.id}`}>
                <div className="grid md:grid-cols-3 gap-3 mb-4">
                  <div>
                    <p className="text-xs font-bold text-zinc-500 uppercase">Organizatör</p>
                    <p className="font-bold mt-0.5">{it.organizer_name}</p>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-zinc-500 uppercase">Mekân</p>
                    <p className="font-bold mt-0.5">{it.venue_name}</p>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-zinc-500 uppercase">Tarih</p>
                    <p className="text-sm mt-0.5">{new Date(it.created_at).toLocaleString('tr-TR')}</p>
                  </div>
                </div>

                {it.free_text && (
                  <div className="bg-zinc-50 border-l-4 border-zinc-300 rounded-r-lg px-3 py-2 mb-4 text-sm italic text-zinc-700">
                    &ldquo;{it.free_text}&rdquo;
                  </div>
                )}

                <div className="flex flex-wrap gap-3 mb-4">
                  <button onClick={() => setOpenDetails(p => ({ ...p, [it.id]: !p[it.id] }))}
                    data-testid={`toggle-details-${it.id}`}
                    className="text-xs font-bold text-[#1E3A8A] hover:underline">
                    {openDetails[it.id] ? '▼' : '▶'} Filtre detayları
                  </button>
                  <button onClick={() => setOpenHistory(p => ({ ...p, [it.id]: !p[it.id] }))}
                    data-testid={`toggle-history-${it.id}`}
                    className="text-xs font-bold text-zinc-600 hover:underline">
                    {openHistory[it.id] ? '▼' : '▶'} Geçmiş
                  </button>
                </div>

                {openDetails[it.id] && (
                  <div className="mb-4">
                    <FiltersSummary applied={it.applied_filters} extracted={it.extracted_filters} />
                  </div>
                )}
                {openHistory[it.id] && (
                  <div className="mb-4 bg-zinc-50 border border-zinc-200 rounded-lg p-3">
                    <HistoryTimeline interestId={it.id} />
                  </div>
                )}

                {/* Editor */}
                <div className="bg-zinc-50 border border-zinc-100 rounded-xl p-4 mb-4">
                  <p className="text-sm font-bold mb-3">Durum & Notlar</p>
                  <div className="grid sm:grid-cols-12 gap-3">
                    <div className="sm:col-span-4">
                      <label className="block text-xs font-semibold text-zinc-600 mb-1">Statü</label>
                      <select value={d.status}
                        onChange={e => setDraft(it.id, { status: e.target.value })}
                        data-testid={`interest-status-${it.id}`}
                        className="w-full px-3 py-2 border-2 border-zinc-200 rounded-lg focus:outline-none focus:border-[#1E3A8A] text-sm">
                        {Object.entries(STATUS_LABELS).map(([k, v]) => (
                          <option key={k} value={k}>{v}</option>
                        ))}
                      </select>
                    </div>
                    <div className="sm:col-span-8">
                      <label className="flex items-center gap-1.5 text-xs font-semibold text-zinc-600 mb-1">
                        <Lock size={12} strokeWidth={1.75} className="text-zinc-500" />
                        İç admin notu
                        <span className="text-zinc-400 font-normal ml-0.5">(sadece admin görür)</span>
                      </label>
                      <textarea rows={2} value={d.admin_note_internal}
                        onChange={e => setDraft(it.id, { admin_note_internal: e.target.value })}
                        data-testid={`interest-internal-${it.id}`}
                        className="w-full px-3 py-2 border border-zinc-200 rounded-xl focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 text-sm transition-all" />
                    </div>
                    <div className="sm:col-span-6">
                      <label className="flex items-center gap-1.5 text-xs font-semibold text-zinc-600 mb-1">
                        <ShieldAlert size={12} strokeWidth={1.75} className="text-amber-600" />
                        Risk notu
                      </label>
                      <textarea rows={2} value={d.admin_note_risk}
                        onChange={e => setDraft(it.id, { admin_note_risk: e.target.value })}
                        data-testid={`interest-risk-${it.id}`}
                        className="w-full px-3 py-2 border border-amber-200 rounded-xl focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 text-sm bg-amber-50/40 transition-all" />
                    </div>
                    <div className="sm:col-span-6">
                      <label className="flex items-center gap-1.5 text-xs font-semibold text-zinc-600 mb-1">
                        <Gem size={12} strokeWidth={1.75} className="text-emerald-600" />
                        Organizatöre gösterilecek
                      </label>
                      <textarea rows={2} value={d.admin_note_for_organizer}
                        onChange={e => setDraft(it.id, { admin_note_for_organizer: e.target.value })}
                        data-testid={`interest-orgnote-${it.id}`}
                        className="w-full px-3 py-2 border border-emerald-200 rounded-xl focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm bg-emerald-50/30 transition-all" />
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-2 mt-3">
                    {dirty && <span className="text-xs text-amber-700 font-semibold">● Değişiklikler kaydedilmedi</span>}
                    <button onClick={() => save(it)} disabled={!dirty || busyById[it.id]}
                      data-testid={`interest-save-${it.id}`}
                      className="px-5 py-2 bg-[#1E3A8A] hover:bg-[#16307A] text-white rounded-xl font-medium text-sm transition-all duration-300 shadow-sm hover:shadow active:scale-[0.98] disabled:opacity-50">
                      {busyById[it.id] ? 'Kaydediliyor…' : 'Kaydet'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminInterests;
